import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Box,
  Avatar,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  InputAdornment,
  Divider
} from '@mui/material';
import {
  Save as SaveIcon,
  PhotoCamera,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import unifiedApi, { buildApiUrl } from '../../../utils/unifiedApi';
import PlayerPositionsManager from './PlayerPositionsManager';
import WaiverConfigSection from './WaiverConfigSection';
import OrganizationFeatureSettings from '../features/OrganizationFeatureSettings';
import ScoringRulesSettings from './ScoringRulesSettings';
import { useOrganizationBranding } from '../../../contexts/OrganizationBrandingContext';

const OrganizationSettings = ({ canEdit = false }) => {
  const { refresh: refreshBranding } = useOrganizationBranding();
  const [settings, setSettings] = useState({
    name: '',
    subdomain: '',
    timeZone: '',
    logoUrl: '',
    emailAddress: '',
    defaultLanguage: 'en',
    weekStartDay: 'Sun',
    currencySymbol: '$',
    installmentsEnabled: false,
    installmentMinimumAmountCents: 0,
    installmentIntervalWeeks: 4,
    installmentNumberOfPayments: 4
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const timeZones = [
    'UTC',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Toronto',
    'America/Vancouver',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Australia/Sydney'
  ];

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'it', name: 'Italian' },
    { code: 'pt', name: 'Portuguese' }
  ];

  const weekDays = [
    { code: 'Sun', name: 'Sunday' },
    { code: 'Mon', name: 'Monday' },
    { code: 'Tue', name: 'Tuesday' },
    { code: 'Wed', name: 'Wednesday' },
    { code: 'Thu', name: 'Thursday' },
    { code: 'Fri', name: 'Friday' },
    { code: 'Sat', name: 'Saturday' }
  ];

  const currencies = [
    '$', '€', '£', '¥', '₹', 'C$', 'A$', '₽', '₩', '¥'
  ];

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const data = await unifiedApi.get('/api/Tenant/settings');
      setSettings(data);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await unifiedApi.put('/api/Tenant/settings', {
        name: settings.name,
        timeZone: settings.timeZone,
        logoUrl: settings.logoUrl,
        emailAddress: settings.emailAddress,
        defaultLanguage: settings.defaultLanguage,
        weekStartDay: settings.weekStartDay,
        currencySymbol: settings.currencySymbol,
        installmentsEnabled: settings.installmentsEnabled,
        installmentMinimumAmountCents: settings.installmentMinimumAmountCents,
        installmentIntervalWeeks: settings.installmentIntervalWeeks,
        installmentNumberOfPayments: settings.installmentNumberOfPayments
      });

      setMessage({ type: 'success', text: 'Settings updated successfully!' });
      refreshBranding();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please select a valid image file.' });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'File size must be less than 5MB.' });
      return;
    }

    setUploading(true);
    setMessage({ type: '', text: '' });

    try {
      const formData = new FormData();
      formData.append('file', file);
      // No need to append tenantId anymore since it's resolved from context
      // formData.append('tenantId', orgId);

      const data = await unifiedApi.request('/api/Tenant/upload-logo', {
        method: 'POST',
        body: formData
      });
      
      // Update the logoUrl in settings
      handleInputChange('logoUrl', data.logoUrl);
      setMessage({ type: 'success', text: 'Logo uploaded successfully!' });
      refreshBranding();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Organization Settings</Typography>
        <Button
          variant="outlined"
          onClick={fetchSettings}
          startIcon={<RefreshIcon />}
          disabled={loading || saving}
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 3 }}>
          {message.text}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Organization Name */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Organization Name"
              fullWidth
              required
              value={settings.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              disabled={!canEdit || saving}
            />
          </Grid>

          {/* Subdomain (Read-only) */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Subdomain"
              fullWidth
              value={settings.subdomain}
              disabled
              helperText="Subdomain cannot be changed"
            />
          </Grid>

          {/* Email Address */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Email Address"
              type="email"
              fullWidth
              value={settings.emailAddress || ''}
              onChange={(e) => handleInputChange('emailAddress', e.target.value)}
              disabled={!canEdit || saving}
            />
          </Grid>

          {/* Time Zone */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Time Zone</InputLabel>
              <Select
                value={settings.timeZone || ''}
                label="Time Zone"
                onChange={(e) => handleInputChange('timeZone', e.target.value)}
                disabled={!canEdit || saving}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {timeZones.map((tz) => (
                  <MenuItem key={tz} value={tz}>
                    {tz}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Default Language */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Default Language</InputLabel>
              <Select
                value={settings.defaultLanguage}
                label="Default Language"
                onChange={(e) => handleInputChange('defaultLanguage', e.target.value)}
                disabled={!canEdit || saving}
              >
                {languages.map((lang) => (
                  <MenuItem key={lang.code} value={lang.code}>
                    {lang.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Week Start Day */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Week Start Day</InputLabel>
              <Select
                value={settings.weekStartDay}
                label="Week Start Day"
                onChange={(e) => handleInputChange('weekStartDay', e.target.value)}
                disabled={!canEdit || saving}
              >
                {weekDays.map((day) => (
                  <MenuItem key={day.code} value={day.code}>
                    {day.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Currency Symbol */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Currency Symbol</InputLabel>
              <Select
                value={settings.currencySymbol}
                label="Currency Symbol"
                onChange={(e) => handleInputChange('currencySymbol', e.target.value)}
                disabled={!canEdit || saving}
              >
                {currencies.map((currency) => (
                  <MenuItem key={currency} value={currency}>
                    {currency}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Installment Payments */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle1" gutterBottom>
              Installment Payments
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              When enabled, leagues can allow registrants to pay in installments. These settings apply to new
              plans only — changing them does not affect anyone already on a plan.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={!!settings.installmentsEnabled}
                  onChange={(e) => handleInputChange('installmentsEnabled', e.target.checked)}
                  disabled={!canEdit || saving}
                />
              }
              label={settings.installmentsEnabled ? 'Installments Enabled' : 'Installments Disabled'}
            />
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Minimum amount to qualify"
                  type="number"
                  fullWidth
                  value={((settings.installmentMinimumAmountCents || 0) / 100).toFixed(2)}
                  onChange={(e) => handleInputChange('installmentMinimumAmountCents', Math.round(parseFloat(e.target.value || 0) * 100))}
                  disabled={!canEdit || saving || !settings.installmentsEnabled}
                  slotProps={{ input: { startAdornment: <InputAdornment position="start">{settings.currencySymbol}</InputAdornment>, inputProps: { min: 0, step: 0.01 } } }}
                  helperText="Registrations below this total cannot use installments."
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Number of payments"
                  type="number"
                  fullWidth
                  value={settings.installmentNumberOfPayments}
                  onChange={(e) => handleInputChange('installmentNumberOfPayments', parseInt(e.target.value) || 0)}
                  disabled={!canEdit || saving || !settings.installmentsEnabled}
                  slotProps={{ input: { inputProps: { min: 2, max: 12 } } }}
                  helperText="Includes the payment taken at checkout."
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Weeks between payments"
                  type="number"
                  fullWidth
                  value={settings.installmentIntervalWeeks}
                  onChange={(e) => handleInputChange('installmentIntervalWeeks', parseInt(e.target.value) || 0)}
                  disabled={!canEdit || saving || !settings.installmentsEnabled}
                  slotProps={{ input: { inputProps: { min: 1, max: 52 } } }}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Logo */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Organization Logo
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                src={settings.logoUrl ? buildApiUrl(settings.logoUrl) : ''}
                sx={{ width: 80, height: 80 }}
              >
                {settings.name?.charAt(0)?.toUpperCase()}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <TextField
                  label="Logo URL"
                  fullWidth
                  value={settings.logoUrl || ''}
                  onChange={(e) => handleInputChange('logoUrl', e.target.value)}
                  disabled={!canEdit || saving}
                  helperText="Enter a URL to your organization's logo image"
                />
              </Box>
              {canEdit && (
              <IconButton 
                color="primary" 
                aria-label="upload picture" 
                component="label"
                disabled={saving || uploading}
              >
                <input 
                  hidden 
                  accept="image/*" 
                  type="file" 
                  onChange={handleFileUpload}
                />
                {uploading ? <CircularProgress size={24} /> : <PhotoCamera />}
              </IconButton>
              )}
            </Box>
          </Grid>

          {/* Save Button */}
          {canEdit && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                disabled={saving}
                size="large"
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </Box>
          </Grid>
          )}
        </Grid>
      </form>
      </Paper>

      {/* Player Positions Management */}
      <Box sx={{ mt: 3 }}>
        <PlayerPositionsManager canEdit={canEdit} />
      </Box>

      {/* Feature switches */}
      <Box sx={{ mt: 3 }}>
        <OrganizationFeatureSettings canEdit={canEdit} />
      </Box>

      {/* Scoring rules */}
      <Box sx={{ mt: 3 }}>
        <ScoringRulesSettings canEdit={canEdit} />
      </Box>

      {/* Waiver Configuration */}
      <Box sx={{ mt: 3 }}>
        <WaiverConfigSection canEdit={canEdit} />
      </Box>
    </>
  );
};

export default OrganizationSettings;